import { Trans, t } from "@lingui/macro";
import { useEffect, useState } from "react";
import { PublicStyle, Soribot } from "../../models/Soribot";
import { Fire } from "../../services/FireClient";
import { Util } from "../../services/Utils/Util";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import { Loader2 } from "lucide-react";

export const SoribotPublic: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(!!bot.enabledPublicPage);
  const [publicId, setPublicId] = useState("");
  const [passcode, setPasscode] = useState<string>("");
  const [pageStyle, setPageStyle] = useState<PublicStyle>();
  const { toast } = useToast();
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    Fire.bot.getSoribotPage(bot.id).then(p => {
      setPublicId(p?.publicId || "");
      setPasscode(p?.passcode || "");
      setIsEnabled(p?.enabled || false);
    });
  }, [bot])

  return (
    <div className="space-y-6 max-w-2xl p-4">
      <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
        <p><Trans>Public pages enables you to share a page with this soribot to anyone with the url without login.</Trans></p>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox id="enable-public" checked={isEnabled} onCheckedChange={(checked) => setIsEnabled(checked as boolean)} />
        <Label htmlFor="enable-public"><Trans>Enable public page</Trans></Label>
      </div>

      <div className={`space-y-4 ${!isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="space-y-2">
          <p className="font-bold text-sm">
            {publicId ? <a href={Util.getPublicSoribotPageURL(publicId)} target="_blank" className="text-blue-600 hover:underline">{Util.getPublicSoribotPageURL(publicId)}</a> : Util.getPublicSoribotPageURL("")}
          </p>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="public-id"><Trans>Public id</Trans></Label>
            <Input
              id="public-id"
              placeholder={t`Enter a unique id`}
              maxLength={20}
              value={publicId}
              onChange={e => setPublicId(e.target.value?.toLowerCase() as string)}
            />
            <p className="text-xs text-gray-500"><Trans>At least 3 characters, only lowercase letters, numbers and dashes.</Trans></p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="passcode"><Trans>Passcode</Trans></Label>
            <Input
              id="passcode"
              placeholder={t`Enter up to 6 digits`}
              maxLength={6}
              type="tel"
              value={passcode}
              onChange={e => setPasscode(e.target.value as string)}
            />
            <p className="text-xs text-gray-500"><Trans>Optional. Leave it empty to allow anyone to enter the page.</Trans></p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label><Trans>Page style</Trans></Label>
            <Select value={pageStyle ?? bot.style ?? 'default-style'} onValueChange={(val) => setPageStyle(val as PublicStyle)}>
              <SelectTrigger>
                <SelectValue placeholder={t`Select style`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default-style"><Trans>Default style</Trans></SelectItem>
                <SelectItem value="bubble-style"><Trans>Bubble style</Trans></SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="pt-4">
        {!inProgress && <Button disabled={disableSave()} onClick={save}><Trans>Save</Trans></Button>}
        {inProgress && <Loader2 className="animate-spin" />}
      </div>
    </div>
  );

  function disableSave() {
    return isEnabled && publicId.length < 3;
  }

  function readyToSaveChanges(value: string) {
    if (isEnabled) {
      // check if the public id is a url friendly string
      if (value.length < 3) {
        return t`Please enter at least 3 characters.`;
      } else if (value.length > 0 && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) {
        return t`Only lowercase letters, numbers and dashes are allowed.`;
      }
      // check if passcode only contains digits.
      if (passcode.length > 0 && !/^[0-9]+$/.test(passcode)) {
        return t`Passcode can only contain digits.`;
      }
    }
    return "";
  }

  async function save() {
    const error = readyToSaveChanges(publicId);
    if (error) {
      toast({ variant: "destructive", description: error });
    } else {
      try {
        setInProgress(true);
        const resp = await Fire.bot.setSoribotPage({ soribotId: bot.id, enabled: isEnabled, publicId: publicId, password: passcode })
        if (resp.error) {
          toast({ variant: "destructive", description: resp.error });
        } else if (resp.status === 'PUBLIC_ID_TAKEN') {
          toast({ variant: "destructive", description: t`This public id is already taken. Please try another one.` });
        } else {
          if (pageStyle) {
            await Fire.bot.updatePageStyle(bot.id, pageStyle)
          }
          toast({ description: t`Settings saved successfully.` });
        }
      } catch (error: any) {
        toast({ variant: "destructive", description: error.message });
      } finally {
        setInProgress(false);
      }
    }
  }
}
export default SoribotPublic;